import CircleIcon from "@mui/icons-material/Circle";
import { Tooltip } from "@mui/material";
import React, { memo } from "react";
import { Base } from "../../../framework/base";
import { TransportOrderState, transportOrderStates } from "../../../models/transport/transportOrder";
import { TransportPlanState, transportPlanStates } from "../../../models/transport/transportPlan";
import { EmployeeListItemDto } from "../../../models/employee/employee";
import { useTranslation } from "react-i18next";

const getDayFormat = () => new Intl.DateTimeFormat(appConfig.culture ?? "fi", {
    weekday: "short",
    day: "numeric",
    month: "numeric",
});

const getTimeFormat = () => new Intl.DateTimeFormat(appConfig.culture ?? "fi", {
    hour: "numeric",
    minute: "numeric"
});

type transportDate = string | Date | null | undefined;

function dateParts(date: transportDate): {day: string; time: string} | undefined {
    if (!date) return undefined;
    if (typeof date === "string") {
        date = new Date(date);
    }
    return {
        day: Base.capitalizeFirstChar(getDayFormat().format(date)),
        time: getTimeFormat().format(date)
    };
}

export function transportDateTimeRange(start: transportDate, end: transportDate): string {
    const startDate = dateParts(start);
    const endDate = dateParts(end);

    if (startDate && endDate && startDate?.day === endDate?.day) {
        return `${startDate.day} ${startDate.time}-${endDate.time}`;
    }

    return [startDate?.day, startDate?.time, " - ", endDate?.day, endDate?.time].filter(s => s).join(" ");
}

export const TransportOrderStateIndicator = memo((props: {state: TransportOrderState; createdDatetime: string; performedDatetime: string}) => {
    const { t } = useTranslation();
    const state = transportOrderStates[props.state];

    if (!state) {
        return (
            <Tooltip title={t("transport.order.unknownState")}>
                <CircleIcon fontSize="small" htmlColor="#ccc" />
            </Tooltip>
        );
    }

    const time = Number(state.value) > 0 ? getDateTime(props.performedDatetime) : getDateTime(props.createdDatetime);
    return (
        <Tooltip title={t(state.name) + ": " + time} disableInteractive>
            <CircleIcon fontSize="small" htmlColor={state.color} />
        </Tooltip>
    );
});

export const TransportPlanStateIndicator = memo((props: {state: TransportPlanState}) => {
    const state = transportPlanStates[props.state];
    return (
        <Tooltip title={state.name} disableInteractive>
            <CircleIcon fontSize="small" htmlColor={state.color} className="mr-1"/>
        </Tooltip>
    );
});

export const getDate = (date: string) => {
    if(!date) return "-";
    return Base.dayjsToDateStr(date);
};

export const getDateTime = (date: string | null | undefined) => {
    if(!date) return "-";
    return Base.dayjsDateToDateTimeStr(date);
};

export const getEmployeeName = (employeeId: string, employees: EmployeeListItemDto[]) => {
    if(!employeeId) return "-";
    const employee = employees.find(e => e.id === employeeId);
    return `${employee?.firstName} ${employee?.lastName}`;
};
